/* eslint-disable no-use-before-define */
import React, { useEffect, useState } from 'react';

import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import NButton from '../../components/inputs/NButton';
import NDatePicker from '../../components/inputs/NDatePicker';
import useCustomerAPI from '../../hooks/network/useCustomerAPI';
import { RouteEnums } from '../../Routes/RoutesConstant';
import NSearch from '../../components/inputs/NSearch';
import useSearchHook from '../../hooks/custom-hooks/useSearchHook';
import NTable2 from '../../components/table/NTable2';
import MobileEdit from '../../assets/svg/MobileEdit';
import MobileDelete from '../../assets/svg/MobileDelete';
import { errorToaster, successToaster } from '../../components/toaster/Toaster';
import moment from 'moment';

function ManageCustomers() {
  const navigate = useNavigate();
  const [customerList, setCustomerList] = useState();
  const [auditReportStartDate, setAuditReportStartDate] = useState();
  const [auditReportEndDate, setAuditReportEndDate] = useState();
  const { downloadAuditReport, deleteCustomer, isLoading: isSubmitting } = useCustomerAPI();
  const { getCustomers, isLoading } = useCustomerAPI();
  const { searchValue, setSearchValue, searchHelper } = useSearchHook();

  const columns = [
    {
      width: '50%',
      key: 'customerName',
      title: 'Customers',
      isSortable: true,

      rowRender: (rowData) => <span> {rowData?.customerName} </span>,
    },
    {
      width: '25%',
      key: 'sapid',
      title: 'Sap ID',
      rowRender: (rowData) => <span> {rowData?.sapid} </span>,
    },
    {
      width: '25%',
      key: 'Actions',
      title: 'Actions',
      columClassName: 'flex justify-end',
      rowRender: (rowData) => (
        <div>
          {/* Mobile */}
          <div className='flex items-center justify-end gap-6 break:hidden'>
            <div onClick={() => onClickEdit(rowData?.id)}>
              <MobileEdit />
            </div>
            <div onClick={() => onClickDelete(rowData?.id)}>
              <MobileDelete />
            </div>
          </div>

          {/* Desktop */}
          <div className='hidden gap-4 break:flex'>
            <NButton
              className='action-edit-btn'
              variant='outlined'
              startIcon={<EditIcon />}
              onClick={() => onClickEdit(rowData?.id)}
            >
              Edit
            </NButton>
            <NButton
              className='action-delete-btn'
              variant='outlined'
              startIcon={<DeleteIcon />}
              onClick={() => onClickDelete(rowData?.id)}
            >
              Delete
            </NButton>
          </div>
        </div>
      ),
    },
  ];

  useEffect(() => {
    fetchCustomers();
  }, []);

  const onClickEdit = (id) => {
    navigate(`${RouteEnums.CUSTOMER_EDIT}/${id}`);
  };

  const onClickDelete = (id) => {
    const response = window.confirm('Press ok to delete customer?');
    if (response) handleDeleteCustomer(id);
  };

  const fetchCustomers = async () => {
    try {
      const response = await getCustomers();
      if (response) {
        setCustomerList(response);
      }
    } catch {}
  };

  const handleDeleteCustomer = async (customerId) => {
    try {
      const response = await deleteCustomer(customerId);
      if (!response) return;
      successToaster(response?.message || 'Deleted Successfully');
      fetchCustomers();
    } catch {}
  };

  const getRecords = () => {
    if (searchValue) {
      return searchHelper(customerList, ['customerName']);
    }
    return customerList;
  };

  const handleDownloadAuditReport = async () => {
    try {
      if (!auditReportStartDate || !auditReportEndDate) {
        errorToaster('Please select start and end date');
        return;
      }
      const sDate = moment(auditReportStartDate).format('MM/DD/YYYY');
      const eDate = moment(auditReportEndDate).format('MM/DD/YYYY');

      const r = await downloadAuditReport(sDate, eDate);

      const url = window.URL.createObjectURL(new Blob([r]));

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Audit report - ${sDate} - ${eDate}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {}
  };

  return (
    <section className='n-table-wrapper'>
      {/* Mobile Header */}
      <div className='break:hidden'>
        <div className='mb-4 flex items-center justify-between'>
          <h2 className='font-semibold'>Customers</h2>
          <div>
            <div>
              <NButton variant='Noutline' onClick={() => navigate(RouteEnums.CUSTOMER_ADD)}>
                Add Customer
              </NButton>
            </div>
          </div>
        </div>
        <div className='mb-4'>
          <NSearch value={searchValue} onChange={(e) => setSearchValue(e?.target?.value)} />
        </div>
      </div>

      {/* Desktop Header */}
      <div
        className={clsx(
          'mb-10 hidden w-full flex-wrap items-start justify-start gap-8 pt-2 macmd:justify-between maclg:gap-4',
          'break:flex',
        )}
      >
        <div>
          <NSearch value={searchValue} onChange={(e) => setSearchValue(e?.target?.value)} />
        </div>
        <div className='flex flex-wrap gap-4'>
          <div className='w-[200px]'>
            <NDatePicker
              onChange={(e) => setAuditReportStartDate(e)}
              label='Start Date*'
              value={auditReportStartDate}
              date={auditReportStartDate}
              className='w-full'
              name='startDate'
              placeholder='DD/MM/YYYY'
              dateFormat='dd/MM/yyyy'
              onCalendarClose={() => {}}
            />
          </div>
          <div className='w-[200px]'>
            <NDatePicker
              onChange={(e) => setAuditReportEndDate(e)}
              value={auditReportEndDate}
              date={auditReportEndDate}
              label='End Date*'
              className='w-full'
              name='startDate'
              placeholder='DD/MM/YYYY'
              dateFormat='dd/MM/yyyy'
              onCalendarClose={() => {}}
            />
          </div>
          <div>
            <NButton
              onClick={handleDownloadAuditReport}
              isLoading={isSubmitting}
              variant='Noutline'
              className='!px-5'
            >
              Download Audit Report
            </NButton>
          </div>
          <div>
            <NButton variant='Ncontained' onClick={() => navigate(RouteEnums.CUSTOMER_ADD)}>
              Add Customer
            </NButton>
          </div>
        </div>
      </div>

      <div className={`${!isLoading ? 'ntable-container' : ''}`}>
        <NTable2 isLoading={isLoading} rows={getRecords()} columns={columns} />
      </div>
    </section>
  );
}

ManageCustomers.propTypes = {};

export default ManageCustomers;
